import api from '../axiosConfig';
import { FINANCE } from '../config/apiEndpoints';

export const FinanceService = {
    /**
     * Get the financial overview dashboard metrics
     * GET /api/v1/finance/dashboard
     * @param {Object} params - Query parameters (startDate, endDate)
     */
    getFinanceDashboard: async (params = {}) => {
        try {
            const response = await api.get(FINANCE.DASHBOARD, { params });
            // API interceptor automatically attaches x-tenant-id header
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService getFinanceDashboard error:', error);
            throw error;
        }
    },

    /**
     * Get financial trends (revenue vs expenses)
     * GET /api/v1/finance/financial-trends
     */
    getFinancialTrends: async (params = {}) => {
        try {
            const response = await api.get(FINANCE.FINANCIAL_TRENDS, { params });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService getFinancialTrends error:', error);
            throw error;
        }
    },

    /**
     * Get transaction ledger list with pagination
     * GET /api/v1/finance/transactions
     * @param {Object} params - Query parameters (page, limit)
     */
    getFinanceTransactions: async (params = {}) => {
        try {
            const response = await api.get(FINANCE.TRANSACTIONS, { params });
            return response.data;
        } catch (error) {
            console.error('FinanceService getFinanceTransactions error:', error);
            throw error;
        }
    },

    /**
     * Get Guest Folio Ledger details
     * GET /api/v1/finance/folios/{id}
     * @param {string} id - Reservation ID / Folio ID
     */
    getGuestFolioLedger: async (id) => {
        try {
            const response = await api.get(FINANCE.FOLIO_BY_ID(id));
            const rawData = response.data?.data || response.data || response;
            let folios = [];
            if (Array.isArray(rawData)) {
                folios = rawData;
            } else if (rawData?.folio && Array.isArray(rawData.folio)) {
                folios = rawData.folio;
            } else if (rawData?.folios && Array.isArray(rawData.folios)) {
                folios = rawData.folios;
            } else if (rawData?.folio && typeof rawData.folio === 'object') {
                folios = [rawData.folio];
            } else if (rawData) {
                folios = [rawData];
            }
            return {
                primaryFolio: folios[0] || null,
                folios: folios,
                rawReservation: (!Array.isArray(rawData) && !rawData?.folios && !rawData?.folio) ? null : rawData,
                billingMode: response.data?.billingMode || rawData?.billingMode || 'master'
            };
        } catch (error) {
            console.error('FinanceService getGuestFolioLedger error:', error);
            throw error;
        }
    },

    /**
     * Add manual charge to Guest Folio
     * POST /api/v1/finance/folios/{id}/charges
     * @param {string} id - Reservation ID / Folio ID
     * @param {Object} payload - { category, description, amount }
     */
    addFolioCharge: async (id, payload, reservationRoomId) => {
        try {
            const config = reservationRoomId ? { params: { reservationRoomId } } : {};
            const response = await api.post(FINANCE.FOLIO_CHARGES(id), payload, config);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService addFolioCharge error:', error);
            throw error;
        }
    },

    /**
     * Post a Payment to Guest Folio
     * POST /api/v1/finance/folios/{id}/payments
     * @param {string} id - Reservation ID / Folio ID
     * @param {Object} payload - { amount, paymentMethod, referenceNumber }
     */
    addFolioPayment: async (id, payload) => {
        try {
            const response = await api.post(FINANCE.FOLIO_PAYMENTS(id), payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService addFolioPayment error:', error);
            throw error;
        }
    },

    /**
     * Process Guest Refund
     * POST /api/v1/finance/folios/{id}/refunds
     * @param {string} id - Reservation ID / Folio ID
     * @param {Object} payload - { amount, refundMethod, reason }
     */
    processFolioRefund: async (id, payload) => {
        try {
            const response = await api.post(FINANCE.FOLIO_PAYMENTS(id), payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService processFolioRefund error:', error);
            throw error;
        }
    },

    /**
     * Log Expense Record
     * POST /api/v1/finance/expenses
     * @param {Object} payload - { category, description, amount, date, paymentMethod, referenceNumber }
     */
    logExpense: async (payload) => {
        try {
            const response = await api.post(FINANCE.EXPENSES, payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService logExpense error:', error);
            throw error;
        }
    },

    /**
     * List logged Expenses
     * GET /api/v1/finance/expenses
     */
    getExpenses: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(FINANCE.EXPENSES, { params: axiosParams });
            if (params.page !== undefined) return response.data;
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService getExpenses error:', error);
            throw error;
        }
    },

    /**
     * Run Daily Night Audit
     * POST /api/v1/finance/night-audit
     */
    runNightAudit: async () => {
        try {
            const response = await api.post(FINANCE.NIGHT_AUDIT);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService runNightAudit error:', error);
            throw error;
        }
    },

    /**
     * Get Night Audit Closes History
     * GET /api/v1/finance/night-audit/history
     */
    getNightAuditHistory: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(FINANCE.NIGHT_AUDIT_HISTORY, { params: axiosParams });
            if (params.page !== undefined) return response.data;
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService getNightAuditHistory error:', error);
            throw error;
        }
    },

    /**
     * Audit Expected Arrivals
     * GET /api/v1/finance/night-audit/expected-arrivals
     */
    getExpectedArrivals: async () => {
        try {
            const response = await api.get(FINANCE.NIGHT_AUDIT_EXPECTED_ARRIVALS);
            return response.data?.data || response.data || [];
        } catch (error) {
            console.error('FinanceService getExpectedArrivals error:', error);
            throw error;
        }
    },

    /**
     * Audit Expected Departures
     * GET /api/v1/finance/night-audit/expected-departures
     */
    getExpectedDepartures: async () => {
        try {
            const response = await api.get(FINANCE.NIGHT_AUDIT_EXPECTED_DEPARTURES);
            return response.data?.data || response.data || [];
        } catch (error) {
            console.error('FinanceService getExpectedDepartures error:', error);
            throw error;
        }
    },

    /**
     * Verify Room Status Discrepancies
     * GET /api/v1/finance/night-audit/room-status-discrepancies
     */
    getRoomStatusDiscrepancies: async () => {
        try {
            const response = await api.get(FINANCE.NIGHT_AUDIT_ROOM_DISCREPANCIES);
            return response.data?.data || response.data || [];
        } catch (error) {
            console.error('FinanceService getRoomStatusDiscrepancies error:', error);
            throw error;
        }
    },

    /**
     * Audit Unsettled Folios
     * GET /api/v1/finance/night-audit/unsettled-folios
     */
    getUnsettledFolios: async () => {
        try {
            const response = await api.get(FINANCE.NIGHT_AUDIT_UNSETTLED_FOLIOS);
            return response.data?.data || response.data || [];
        } catch (error) {
            console.error('FinanceService getUnsettledFolios error:', error);
            throw error;
        }
    },

    /**
     * City Ledger Validation
     * GET /api/v1/finance/night-audit/city-ledger
     */
    getCityLedger: async () => {
        try {
            const response = await api.get(FINANCE.NIGHT_AUDIT_CITY_LEDGER);
            return response.data?.data || response.data || [];
        } catch (error) {
            console.error('FinanceService getCityLedger error:', error);
            throw error;
        }
    },

    /**
     * Reconcile Daily Payments
     * GET /api/v1/finance/night-audit/reconcile-payments
     */
    getReconcilePayments: async () => {
        try {
            const response = await api.get(FINANCE.NIGHT_AUDIT_RECONCILE_PAYMENTS);
            return response.data?.data || response.data || [];
        } catch (error) {
            console.error('FinanceService getReconcilePayments error:', error);
            throw error;
        }
    },

    /**
     * Generate Night Audit Report
     * GET /api/v1/finance/night-audit/generate-report
     */
    generateNightAuditReport: async (params = {}) => {
        try {
            const response = await api.get(FINANCE.NIGHT_AUDIT_GENERATE_REPORT, { 
                params,
                responseType: 'blob' 
            });
            return response;
        } catch (error) {
            console.error('FinanceService generateNightAuditReport error:', error);
            throw error;
        }
    },

    /**
     * Generate Exception Report
     * GET /api/v1/finance/night-audit/exception-report
     */
    generateExceptionReport: async (params = {}) => {
        try {
            const response = await api.get(FINANCE.NIGHT_AUDIT_EXCEPTION_REPORT, { 
                params,
                responseType: 'blob'
            });
            return response;
        } catch (error) {
            console.error('FinanceService generateExceptionReport error:', error);
            throw error;
        }
    },

    /**
     * Detect Unposted Charges
     * GET /api/v1/finance/night-audit/unposted-charges
     */
    getUnpostedCharges: async () => {
        try {
            const response = await api.get(FINANCE.NIGHT_AUDIT_UNPOSTED_CHARGES);
            return response.data?.data || response.data || [];
        } catch (error) {
            console.error('FinanceService getUnpostedCharges error:', error);
            throw error;
        }
    },

    /**
     * Audit Voided Transactions
     * GET /api/v1/finance/night-audit/void-tracker
     */
    getVoidTracker: async () => {
        try {
            const response = await api.get(FINANCE.NIGHT_AUDIT_VOID_TRACKER);
            return response.data?.data || response.data || [];
        } catch (error) {
            console.error('FinanceService getVoidTracker error:', error);
            throw error;
        }
    },

    /**
     * Export Ledger to external software formatting
     * GET /api/v1/finance/export
     * @param {Object} params - Query parameters (startDate, endDate, format)
     */
    exportLedger: async (params = {}) => {
        try {
            const response = await api.get(FINANCE.EXPORT, { 
                params
            });
            return response.data;
        } catch (error) {
            console.error('FinanceService exportLedger error:', error);
            throw error;
        }
    },

    /**
     * Edit a specific Folio Charge
     * PUT /api/v1/finance/folios/{id}/charges/{chargeId}
     * @param {string} id - Reservation ID / Folio ID
     * @param {string} chargeId - Charge ID to edit
     * @param {Object} payload - { description, amount }
     */
    editFolioCharge: async (id, chargeId, payload) => {
        try {
            const response = await api.put(FINANCE.EDIT_FOLIO_CHARGE(id, chargeId), payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService editFolioCharge error:', error);
            throw error;
        }
    },

    /**
     * Void a specific Folio Charge
     * DELETE /api/v1/finance/folios/{id}/charges/{chargeId}
     * @param {string} id - Reservation ID / Folio ID
     * @param {string} chargeId - Charge ID to void
     * @param {Object} payload - { voidReason }
     */
    voidFolioCharge: async (id, chargeId, payload) => {
        try {
            const response = await api.delete(FINANCE.VOID_FOLIO_CHARGE(id, chargeId), { data: payload });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService voidFolioCharge error:', error);
            throw error;
        }
    },

    /**
     * Get Open Cashier Shifts
     * GET /api/v1/finance/shifts/active
     */
    getOpenCashierShifts: async (tenantId) => {
        try {
            const response = await api.get(FINANCE.SHIFTS_ACTIVE, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response || [];
        } catch (error) {
            console.error('FinanceService getOpenCashierShifts error:', error);
            throw error;
        }
    },

    /**
     * Open a new Cashier Shift
     * POST /api/v1/finance/shifts/open
     */
    openCashierShift: async (tenantId, payload) => {
        try {
            const response = await api.post(FINANCE.SHIFTS_OPEN, payload, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService openCashierShift error:', error);
            throw error;
        }
    },

    /**
     * Declare Till and Close Cashier Shift
     * POST /api/v1/finance/shifts/declare
     */
    closeCashierShift: async (tenantId, payload) => {
        try {
            const response = await api.post(FINANCE.SHIFTS_DECLARE, payload, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService closeCashierShift error:', error);
            throw error;
        }
    },

    /**
     * Get Shift Liquidity Summary
     * GET /api/v1/finance/shifts/:id/liquidity-summary
     * Permission: finance.view_financials
     */
    getShiftLiquiditySummary: async (arg1, arg2) => {
        let shiftId = arg1;
        let tenantId = arg2;
        // Resilient parameter handling if passed as (tenantId, shiftId)
        if (typeof arg1 === 'string' && typeof arg2 === 'string') {
            if (!arg1.includes('-') && arg1.length <= 4 && (arg2.includes('-') || arg2.startsWith('shift') || arg2.length > 4)) {
                shiftId = arg2;
                tenantId = arg1;
            }
        }
        try {
            const headers = tenantId ? { 'x-tenant-id': tenantId } : {};
            const response = await api.get(FINANCE.SHIFT_LIQUIDITY_SUMMARY(shiftId), { headers });
            return response.data?.data || response.data;
        } catch (error) {
            console.error(`FinanceService getShiftLiquiditySummary (${shiftId}) error:`, error);
            throw error;
        }
    },

    /**
     * Cash Drop to Safe
     * POST /api/v1/finance/shifts/cash-drop
     * Permission: finance.declare_till
     */
    createCashDrop: async (tenantId, payload) => {
        try {
            const headers = tenantId ? { 'x-tenant-id': tenantId } : {};
            const response = await api.post(FINANCE.SHIFTS_CASH_DROP, payload, { headers });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('FinanceService createCashDrop error:', error);
            throw error;
        }
    }
};
